import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, X, Image as ImageIcon, Upload, AlertTriangle } from 'lucide-react';
import { BACKEND_URL, API_URL } from '../../config/api';
import { TEMPLATE_DISCLAIMER } from '../../constants/disclaimers';
import { getImageUrl } from '../../utils/imageUrl';
import './ManageTemplates.css';

const ManageTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    description: '',
    code: '',
    visibilityTiers: [],
    ebookUrl: '',
    youtubeUrl: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [categories, setCategories] = useState([]);
  const [customCategory, setCustomCategory] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [customSubcategory, setCustomSubcategory] = useState('');

  const tiers = ['free', 'vip', 'premium'];

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/admin/templates/categories`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        // Use the 'all' array which contains predefined + custom categories
        setCategories(data.data.all || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to predefined categories if fetch fails
      setCategories([
        'Moving Averages',
        'Oscillators',
        'Volatility',
        'Trend',
        'Volume',
        'Support/Resistance',
        'Patterns',
        'Other'
      ]);
    }
  };

  const fetchSubcategories = async (categoryName) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/admin/templates/categories/${encodeURIComponent(categoryName)}/subcategories`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setSubcategories(data.data.subcategories || []);
      } else {
        setSubcategories([]);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/admin/templates`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        console.log('📋 Templates fetched:', data.data.length);
        console.log('📝 First template code check:');
        if (data.data.length > 0) {
          console.log('  Name:', data.data[0].name);
          console.log('  Code:', data.data[0].code);
          console.log('  Code length:', data.data[0].code?.length || 0);
          console.log('  Code type:', typeof data.data[0].code);
        }
        setTemplates(data.data);

      } else {
        showToast(data.message || 'Failed to fetch templates', 'error');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, []); // ✅ CORRECT - empty dependency array

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({
      name: '',
      category: '',
      subcategory: '',
      description: '',
      code: '',
      visibilityTiers: [],
      ebookUrl: '',
      youtubeUrl: ''
    });
    setCustomCategory('');
    setSubcategories([]);
    setCustomSubcategory('');
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setErrors({});
    setShowModal(true);
  };

  const handleOpenEditModal = async (template) => {
    console.log('📝 Opening edit modal for template:', template.name);
    console.log('💻 Template code from DB:', template.code);
    console.log('📏 Template code length:', template.code?.length || 0);
    console.log('🔍 Template code type:', typeof template.code);

    setModalMode('edit');
    setSelectedTemplate(template);

    // Check if template category is in the categories list
    const isCustomCategory = !categories.includes(template.category);
    const actualCategory = isCustomCategory ? template.category : (template.category === 'Other' ? template.category : template.category);

    // Fetch subcategories if category exists and is not "Other"
    if (actualCategory && actualCategory !== 'Other') {
      await fetchSubcategories(actualCategory);
    }

    // Check if template has subcategory
    const hasSubcategory = template.subcategory;
    const isCustomSubcategory = hasSubcategory && actualCategory && actualCategory !== 'Other' ? true : false;

    setFormData({
      name: template.name,
      category: isCustomCategory ? 'Other' : template.category,
      subcategory: hasSubcategory && !isCustomSubcategory ? template.subcategory : '',
      description: template.description,
      code: template.code,
      visibilityTiers: template.visibilityTiers,
      ebookUrl: template.ebookUrl || '',
      youtubeUrl: template.youtubeUrl || ''
    });

    // Set custom category if it's a custom one
    setCustomCategory(isCustomCategory ? template.category : '');

    // Set custom subcategory if it exists
    setCustomSubcategory(hasSubcategory && isCustomSubcategory ? template.subcategory : '');

    console.log('✅ FormData set with code length:', template.code?.length || 0);

    setImageFile(null);
    setImagePreview(template.imageUrl ? getImageUrl(template.imageUrl) : null);
    setRemoveImage(false);
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setCustomCategory('');
    setSubcategories([]);
    setCustomSubcategory('');
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTierToggle = (tier) => {
    setFormData(prev => {
      const tiers = prev.visibilityTiers.includes(tier)
        ? prev.visibilityTiers.filter(t => t !== tier)
        : [...prev.visibilityTiers, tier];
      return { ...prev, visibilityTiers: tiers };
    });
    // Clear error for visibility tiers
    if (errors.visibilityTiers) {
      setErrors(prev => ({ ...prev, visibilityTiers: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please upload a valid image file (JPG, PNG, GIF, or WEBP)', 'error');
      e.target.value = '';
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      showToast('Image size must be less than 5MB', 'error');
      e.target.value = '';
      return;
    }

    setImageFile(file);
    setRemoveImage(false);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Clear error
    if (errors.image) {
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Template name is required';
    if (!formData.category) newErrors.category = 'Category is required';

    // Validate custom category when "Other" is selected
    if (formData.category === 'Other' && !customCategory.trim()) {
      newErrors.customCategory = 'Custom category name is required';
    }

    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (formData.visibilityTiers.length === 0) {
      newErrors.visibilityTiers = 'At least one tier must be selected';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token"); // ⚠️ WAJIB ada
    if (!token) {
    showToast("Sila login dahulu", "error");
    return;
  }

    if (!validateForm()) {
      showToast('Please fix the errors in the form', 'error');
      return;
    }

    try {
      const url = modalMode === 'create'
        ? `${API_URL}/admin/templates/create`
        : `${API_URL}/admin/templates/${selectedTemplate._id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Use FormData for file upload (optional image)
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);

      // Use custom category if "Other" is selected, otherwise use selected category
      const categoryToSubmit = formData.category === 'Other' ? customCategory : formData.category;
      formDataToSend.append('category', categoryToSubmit);

      // Use custom subcategory if "Other" is selected, otherwise use selected subcategory
      const subcategoryToSubmit = formData.subcategory === 'Other' ? customSubcategory : formData.subcategory;
      if (subcategoryToSubmit) {
        formDataToSend.append('subcategory', subcategoryToSubmit);
      }

      formDataToSend.append('description', formData.description);
      formDataToSend.append('code', formData.code);
      formDataToSend.append('visibilityTiers', JSON.stringify(formData.visibilityTiers));
      formDataToSend.append('ebookUrl', formData.ebookUrl || '');
      formDataToSend.append('youtubeUrl', formData.youtubeUrl || '');

      // Append image if selected (OPTIONAL)
      if (imageFile) {
        formDataToSend.append('templateImage', imageFile);
      }

      // Append removeImage flag if user wants to remove the image
      if (removeImage) {
        formDataToSend.append('removeImage', 'true');
      }

      console.log('📤 Submitting template with image:', !!imageFile);
      console.log('📝 Form data - code field:', formData.code);
      console.log('📏 Code length:', formData.code?.length || 0);
      console.log('🔍 Code type:', typeof formData.code);
      console.log('🔍 FormData entries:');
      for (let [key, value] of formDataToSend.entries()) {
        if (key === 'code') {
          console.log(`  ${key}:`, value?.substring(0, 100) + '... (length: ' + value?.length + ')');
        } else {
          console.log(`  ${key}:`, value);
        }
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Authorization": `Bearer ${token}`
        },
        credentials: 'include',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        showToast(
          modalMode === 'create'
            ? 'Template created successfully'
            : 'Template updated successfully',
          'success'
        );
        handleCloseModal();
        fetchTemplates();
        fetchCategories(); // Refresh categories to include any new custom category
      } else {
        showToast(data.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showToast('Failed to save template', 'error');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        showToast('Template deleted successfully', 'success');
        fetchTemplates();
      } else {
        showToast(data.message || 'Failed to delete template', 'error');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Failed to delete template', 'error');
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'free': return '#4caf50';
      case 'vip': return '#ff9800';
      case 'premium': return '#9c27b0';
      default: return '#666';
    }
  };

  return (
    <div className="manage-templates">
      {/* Header */}
      <div className="templates-header">
        <div>
          <h1>Code Templates Management</h1>
          <p>Create and manage Pine Script templates for users</p>
        </div>
        <button className="btn-create" onClick={handleOpenCreateModal}>
          <Plus size={20} />
          Create New Template
        </button>
      </div>

      {/* Templates List */}
      <div className="templates-container">
        {loading ? (
          <div className="loading-state">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            <p>No templates found. Create your first template to get started.</p>
          </div>
        ) : (
          <div className="templates-table-wrapper">
            <table className="templates-table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Template Name</th>
                  <th>Category</th>
                  <th>Subcategory</th>
                  <th>Visibility</th>
                  <th>Usage Count</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template._id}>
                    <td className="template-image-cell">
                      {template.imageUrl ? (
                        <div
                          className="template-image-thumbnail"
                          onClick={() => handleImageClick(template.imageUrl)}
                          title="Click to view full size"
                        >
                          <img
                            src={getImageUrl(template.imageUrl)}
                            alt={template.name}
                            onError={(e) => {
                              // Only log the error, don't hide the image immediately
                              // This prevents premature hiding during slow loading
                              console.warn('Image failed to load:', getImageUrl(template.imageUrl));
                              e.target.onerror = null; // Prevent infinite loop
                              e.target.style.opacity = '0.3'; // Show failed state instead of hiding
                            }}
                          />
                        </div>
                      ) : (
                        <div className="image-placeholder">
                          <ImageIcon size={24} />
                        </div>
                      )}
                    </td>
                    <td className="template-name">{template.name}</td>
                    <td>{template.category}</td>
                    <td>{template.subcategory || '-'}</td>
                    <td>
                      <div className="tier-badges">
                        {template.visibilityTiers.map((tier) => (
                          <span
                            key={tier}
                            className="tier-badge"
                            style={{ backgroundColor: getTierBadgeColor(tier) }}
                          >
                            {tier.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="usage-count">{template.usageCount || 0}</td>
                    <td>{formatDate(template.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-action btn-preview"
                          onClick={() => handlePreview(template)}
                          title="Preview"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          className="btn-action btn-edit"
                          onClick={() => handleOpenEditModal(template)}
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(template._id)}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Fixed Header */}
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Create New Template' : 'Edit Template'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="modal-body max-h-[90vh] overflow-y-auto overflow-x-hidden">
              <form onSubmit={handleSubmit} className="template-form" id="template-form">
                <div className="form-group">
                  <label htmlFor="name">Template Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'error' : ''}
                    placeholder="Enter template name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={(e) => {
                      handleInputChange(e);
                      const selectedCategory = e.target.value;

                      // Clear custom category when non-Other option selected
                      if (selectedCategory !== 'Other') {
                        setCustomCategory('');
                        // Clear custom category error
                        if (errors.customCategory) {
                          setErrors(prev => ({ ...prev, customCategory: '' }));
                        }

                        // Fetch subcategories for the selected category
                        if (selectedCategory) {
                          fetchSubcategories(selectedCategory);
                        }
                      } else {
                        // Clear subcategories when "Other" is selected
                        setSubcategories([]);
                      }

                      // Reset subcategory selection
                      setFormData(prev => ({ ...prev, subcategory: '' }));
                      setCustomSubcategory('');
                    }}
                    className={errors.category ? 'error' : ''}
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {errors.category && <span className="error-message">{errors.category}</span>}
                </div>

                {/* Custom Category Input - Show when "Other" is selected */}
                {formData.category === 'Other' && (
                  <div className="form-group">
                    <label htmlFor="customCategory">Custom Category (e.g., Divergence) *</label>
                    <input
                      type="text"
                      id="customCategory"
                      name="customCategory"
                      value={customCategory}
                      onChange={(e) => {
                        setCustomCategory(e.target.value);
                        // Clear error for this field
                        if (errors.customCategory) {
                          setErrors(prev => ({ ...prev, customCategory: '' }));
                        }
                      }}
                      className={errors.customCategory ? 'error' : ''}
                      placeholder="Enter custom category name"
                    />
                    {errors.customCategory && <span className="error-message">{errors.customCategory}</span>}
                  </div>
                )}

                {/* Subcategory Dropdown - Show when category is selected and not "Other" */}
                {formData.category && formData.category !== 'Other' && (
                  <div className="form-group">
                    <label htmlFor="subcategory">Subcategory (Optional)</label>
                    <select
                      id="subcategory"
                      name="subcategory"
                      value={formData.subcategory}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Clear custom subcategory when non-Other option selected
                        if (e.target.value !== 'Other') {
                          setCustomSubcategory('');
                          // Clear custom subcategory error
                          if (errors.customSubcategory) {
                            setErrors(prev => ({ ...prev, customSubcategory: '' }));
                          }
                        }
                      }}
                      className={errors.subcategory ? 'error' : ''}
                    >
                      <option value="">Select a subcategory</option>
                      {subcategories.map((subcat) => (
                        <option key={subcat} value={subcat}>{subcat}</option>
                      ))}
                      <option value="Other">Other (Custom)</option>
                    </select>
                    {errors.subcategory && <span className="error-message">{errors.subcategory}</span>}
                  </div>
                )}

                {/* Custom Subcategory Input - Show when "Other" subcategory is selected */}
                {formData.subcategory === 'Other' && (
                  <div className="form-group">
                    <label htmlFor="customSubcategory">Custom Subcategory (e.g., Bullish Divergence) *</label>
                    <input
                      type="text"
                      id="customSubcategory"
                      name="customSubcategory"
                      value={customSubcategory}
                      onChange={(e) => {
                        setCustomSubcategory(e.target.value);
                        // Clear error for this field
                        if (errors.customSubcategory) {
                          setErrors(prev => ({ ...prev, customSubcategory: '' }));
                        }
                      }}
                      className={errors.customSubcategory ? 'error' : ''}
                      placeholder="Enter custom subcategory name"
                    />
                    {errors.customSubcategory && <span className="error-message">{errors.customSubcategory}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter template description (optional)"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ebookUrl">Link URL Ebook (Optional)</label>
                  <input
                    type="url"
                    id="ebookUrl"
                    name="ebookUrl"
                    value={formData.ebookUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/ebook.pdf"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="youtubeUrl">Link URL YouTube (Optional)</label>
                  <input
                    type="url"
                    id="youtubeUrl"
                    name="youtubeUrl"
                    value={formData.youtubeUrl}
                    onChange={handleInputChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="code">Pine Script Code *</label>
                  <textarea
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className={`code-textarea ${errors.code ? 'error' : ''}`}
                    placeholder="// Enter Pine Script code here..."
                    rows="12"
                  />
                  {errors.code && <span className="error-message">{errors.code}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="templateImage">Template Preview Image (Optional)</label>
                  <p className="field-description">
                    Upload a screenshot showing the indicator in action (Max 5MB, JPG/PNG/GIF/WEBP)
                  </p>

                  <div className="image-upload-section">
                    {imagePreview && !removeImage ? (
                      <div className="image-preview-container">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="image-preview"
                        />
                        <button
                          type="button"
                          className="btn-remove-image"
                          onClick={handleRemoveImage}
                        >
                          <X size={18} />
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-input">
                        <label htmlFor="templateImage" className="file-upload-label">
                          <Upload size={20} />
                          <span>Choose Image</span>
                        </label>
                        <input
                          type="file"
                          id="templateImage"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                      </div>
                    )}
                  </div>
                  {errors.image && <span className="error-message">{errors.image}</span>}
                </div>

                <div className="form-group">
                  <label>Visibility Tier Selection *</label>
                  <div className="tier-checkboxes">
                    {tiers.map((tier) => (
                      <label key={tier} className="tier-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.visibilityTiers.includes(tier)}
                          onChange={() => handleTierToggle(tier)}
                        />
                        <span className="tier-label" style={{ color: getTierBadgeColor(tier) }}>
                          {tier.toUpperCase()}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.visibilityTiers && (
                    <span className="error-message">{errors.visibilityTiers}</span>
                  )}
                </div>
              </form>
            </div>

            {/* Fixed Footer */}
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button type="submit" form="template-form" className="btn-save">
                {modalMode === 'create' ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewTemplate && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Template Preview</h2>
              <button className="btn-close" onClick={() => setShowPreviewModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="preview-content">
              {/* Template Image or Placeholder */}
              {previewTemplate.imageUrl ? (
                <div className="preview-image-section">
                  <img
                    src={getImageUrl(previewTemplate.imageUrl)}
                    alt={previewTemplate.name}
                    className="preview-full-image"
                    onClick={() => handleImageClick(previewTemplate.imageUrl)}
                    style={{ cursor: 'pointer' }}
                    title="Click to view full size"
                    onError={(e) => {
                      console.warn('Preview image failed to load:', getImageUrl(previewTemplate.imageUrl));
                      e.target.onerror = null; // Prevent infinite loop
                    }}
                  />
                </div>
              ) : (
                <div className="preview-image-placeholder">
                  <ImageIcon size={64} />
                  <p>No preview image</p>
                </div>
              )}

              {/* Template Info */}
              <div className="preview-info">
                <h3>{previewTemplate.name}</h3>
                <p className="preview-category">Category: {previewTemplate.category}</p>
                {previewTemplate.subcategory && (
                  <p className="preview-category">Subcategory: {previewTemplate.subcategory}</p>
                )}
                {previewTemplate.description && (
                  <p className="preview-description">{previewTemplate.description}</p>
                )}
                <div className="preview-tiers">
                  <strong>Available for:</strong>
                  <div className="tier-badges">
                    {previewTemplate.visibilityTiers.map((tier) => (
                      <span
                        key={tier}
                        className="tier-badge"
                        style={{ backgroundColor: getTierBadgeColor(tier) }}
                      >
                        {tier.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Disclaimer Section */}
              <div className="preview-disclaimer">
                <div className="disclaimer-header">
                  <AlertTriangle size={20} className="disclaimer-icon" />
                  <h4>Disclaimer / Penafian</h4>
                </div>
                <div className="disclaimer-content">
                  {TEMPLATE_DISCLAIMER.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Pine Script Code */}
              <div className="preview-code">
                <h4>Pine Script Code:</h4>
                {previewTemplate.code ? (
                  <pre className="code-preview">{previewTemplate.code}</pre>
                ) : (
                  <div className="code-empty-preview">
                    <p>No code available for this template</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-Size Image Modal */}
      {showImageModal && selectedImage && (
        <div className="modal-overlay image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="btn-close image-modal-close" onClick={() => setShowImageModal(false)}>
              <X size={32} />
            </button>
            <img
              src={getImageUrl(selectedImage)}
              alt="Full size preview"
              className="full-size-image"
              onError={(e) => {
                console.warn('Full-size image failed to load:', getImageUrl(selectedImage));
                e.target.onerror = null; // Prevent infinite loop
              }}
            />
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ManageTemplates;
